import type {
  CreateQuestionBankEntryInput,
  IQuestionBankEntryDetail,
  IQuestionBankQuestionPayload,
} from '@brushfire/types'
import type {
  InstrumentDraftQuestion,
  InstrumentDraftQuestionType,
} from '@/features/instruments/infra/instrumentDraft.api'
import { newObjectIdHex } from '@/features/instruments/domain/objectIdHex'
import { mapBuilderQuestionsToInstrumentDraft } from '@/features/instruments/question-builder/mapBuilderToInstrumentDraft'
import { mapInstrumentDraftToBuilderQuestions } from '@/features/instruments/question-builder/mapInstrumentDraftToBuilder'
import type { BuilderQuestion } from '@/features/instruments/question-builder/types'

function bankPayloadToDraftRow(
  payload: IQuestionBankQuestionPayload,
  type: string,
  prompt: string,
  id = newObjectIdHex(),
): InstrumentDraftQuestion {
  const apiType = type as InstrumentDraftQuestionType
  const rawOptions = payload.options as
    | Array<{ id?: string; label: string; imageUrl?: string }>
    | undefined
  const options = rawOptions?.map((o) => ({
    id: o.id && /^[a-f0-9]{24}$/i.test(o.id) ? o.id : newObjectIdHex(),
    label: o.label,
    ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
  }))

  return {
    id,
    order: payload.order ?? 0,
    type: apiType,
    prompt,
    description: payload.description ?? null,
    mandatory: payload.mandatory ?? false,
    hidden: payload.hidden === true,
    ...(options ? { options } : {}),
    minSelections: payload.minSelections,
    maxSelections: payload.maxSelections,
    yesLabel: payload.yesLabel,
    noLabel: payload.noLabel,
    showSelectAll: payload.showSelectAll,
    selectAllLabel: payload.selectAllLabel,
    randomizeOrder: payload.randomizeOrder,
    layoutHorizontal: payload.layoutHorizontal,
    multiColumn: payload.multiColumn,
    yesNoToggleStyle: payload.yesNoToggleStyle,
    hasOtherOption: payload.hasOtherOption,
    hasNoneOfAboveOption: payload.hasNoneOfAboveOption,
    minLength: payload.minLength,
    maxLength: payload.maxLength,
    placeholder: payload.placeholder,
    imageSize: payload.imageSize,
    earliestSelectableDate: payload.earliestSelectableDate,
    latestSelectableDate: payload.latestSelectableDate,
    disablePastDates: payload.disablePastDates,
    disableFutureDates: payload.disableFutureDates,
    dateInputFormat: payload.dateInputFormat,
    ratingScaleMax: payload.ratingScaleMax,
    ratingDisplayStyle: payload.ratingDisplayStyle,
    ratingLowLabel: payload.ratingLowLabel,
    ratingHighLabel: payload.ratingHighLabel,
    likertPointCount: payload.likertPointCount,
    likertLabels: payload.likertLabels,
    likertLayoutHorizontal: payload.likertLayoutHorizontal,
    sliderMinValue: payload.sliderMinValue,
    sliderMaxValue: payload.sliderMaxValue,
    sliderStep: payload.sliderStep,
    sliderLowLabel: payload.sliderLowLabel,
    sliderHighLabel: payload.sliderHighLabel,
    videoUrl: payload.videoUrl,
    videoSource: payload.videoSource,
  }
}

/** Maps a builder row to POST `/question-bank` body (plain-text prompt for duplicate check). */
export function builderQuestionToBankPayload(q: BuilderQuestion): CreateQuestionBankEntryInput {
  const draft = mapBuilderQuestionsToInstrumentDraft([q])[0]
  const plainPrompt = q.questionText.trim()
  const { id: _id, order: _order, type, prompt: _markdownPrompt, ...rest } = draft
  return {
    prompt: plainPrompt,
    type,
    question: {
      order: 0,
      type,
      prompt: draft.prompt,
      ...rest,
    },
  }
}

/** Stable content fingerprint — ignores option ids so bank ↔ builder compares cleanly. */
export function questionBankContentFingerprint(
  input: Pick<CreateQuestionBankEntryInput, 'prompt' | 'type' | 'question'>,
): string {
  const question = { ...input.question } as Record<string, unknown>
  const options = question.options as Array<{ label: string; imageUrl?: string }> | undefined
  if (Array.isArray(options)) {
    question.options = options.map((o) => ({
      label: o.label,
      ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
    }))
  }
  return JSON.stringify({
    prompt: input.prompt.trim(),
    type: input.type,
    question,
  })
}

export function builderQuestionBankContentFingerprint(q: BuilderQuestion): string {
  return questionBankContentFingerprint(builderQuestionToBankPayload(q))
}

export function bankEntryContentFingerprint(entry: IQuestionBankEntryDetail): string {
  return questionBankContentFingerprint({
    prompt: entry.prompt,
    type: entry.type,
    question: entry.question,
  })
}

/** Clones a bank entry into a new independent builder question (BR-4). */
export function bankEntryToBuilderQuestion(
  entry: IQuestionBankEntryDetail,
  displayIndex: number,
): BuilderQuestion {
  const draftRow = bankPayloadToDraftRow(entry.question, entry.type, entry.question.prompt)
  const mapped = mapInstrumentDraftToBuilderQuestions([{ ...draftRow, order: displayIndex }])[0]
  return {
    ...mapped,
    id: newObjectIdHex(),
    title: `Question ${displayIndex + 1}`,
    fromQuestionBank: true,
    questionBankEntryId: entry.id,
    ...(entry.isProfileUpdate === true
      ? {
          isProfileUpdate: true as const,
          ...(entry.profileAttributeId ? { profileAttributeId: entry.profileAttributeId } : {}),
        }
      : {}),
  }
}

/** Maps a bank entry to a builder question for read-only live preview in the detail modal. */
export function bankEntryToPreviewBuilderQuestion(
  entry: IQuestionBankEntryDetail,
): BuilderQuestion {
  const q = bankEntryToBuilderQuestion(entry, 0)
  return {
    ...q,
    questionText: entry.prompt,
  }
}

function nonEmptyTrimmedLabels(labels: string[]): string[] {
  return labels.map((label) => label.trim()).filter((label) => label.length > 0)
}

function optionLabelsFromOptions(
  opts: Array<{ label: string }> | undefined,
): string[] {
  if (!opts?.length) return []
  return opts.map((o) => o.label).filter((label) => label.trim().length > 0)
}

/** Read-only answer option labels for detail modal. */
export function questionBankAnswerOptionLabels(
  entry: IQuestionBankEntryDetail,
): string[] {
  const { type, question } = entry

  switch (type) {
    case 'yes_no': {
      const yes = question.yesLabel?.trim() || 'Yes'
      const no = question.noLabel?.trim() || 'No'
      return [yes, no]
    }
    case 'likert':
      return nonEmptyTrimmedLabels(question.likertLabels ?? [])
    case 'rating': {
      const max = question.ratingScaleMax ?? 5
      return Array.from({ length: max }, (_, index) => String(index + 1))
    }
    default:
      return optionLabelsFromOptions(question.options)
  }
}

export const OPEN_RESPONSE_TYPES = new Set([
  'short_text',
  'long_text',
  'slider',
  'date',
  'video',
])

export function questionBankHasAnswerOptions(entry: IQuestionBankEntryDetail): boolean {
  if (OPEN_RESPONSE_TYPES.has(entry.type)) return false
  const labels = questionBankAnswerOptionLabels(entry)
  return labels.length > 0
}
